import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronRight, Lightbulb, Loader2, Plus, RefreshCw, Search, SlidersHorizontal, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import {
  PRIORITIES, PRIORITY_LABELS, PRODUCT_CREATE_KEYS, QUICK_VIEWS, STATUSES, STATUS_LABELS, TYPE_LABELS, formatDate, groupRoadmap,
  deliveredInText, pharmaciesText, productErrorMessage,
} from "../lib/product";
import { PriorityBadge, StatusBadge, TypeBadge } from "../components/product/ProductBits";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "../components/ui/responsive-popover";

const ALL = "__all__";
const PAGE_SIZE = 50;
const TABS = [{ id: "items", label: "Retours" }, { id: "roadmap", label: "Feuille de route" }];
const ROADMAP_VIEWS = [{ id: "status", label: "Par état" }, { id: "theme", label: "Par thème" }, { id: "version", label: "Par version" }];

// Filters live in the address (the dashboard, the search palette and a ticket's card land on the same view).
function useFilters() {
  const [params, setParams] = useSearchParams();
  const pick = (name, allowed) => (allowed.includes(params.get(name)) ? params.get(name) : "");
  const filters = {
    tab: params.get("tab") === "roadmap" ? "roadmap" : "items",
    by: pick("by", ["status", "theme", "version"]) || "status",
    status: pick("status", STATUSES),
    state: pick("state", ["open", "delivered", "abandoned"]),
    type: pick("type", Object.keys(TYPE_LABELS)),
    priority: pick("priority", PRIORITIES),
    owner: params.get("owner") === "me" ? "me" : "",
    search: params.get("q") || "",
  };
  const update = useCallback((changes) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(changes)) {
        const name = key === "search" ? "q" : key;
        if (value === "" || value == null) next.delete(name);
        else next.set(name, value);
      }
      return next;
    }, { replace: true });
  }, [setParams]);
  return [filters, update];
}

function MetaLine({ item }) {
  const pharmacies = pharmaciesText(item);
  return (
    <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
      {item.owner_name && <span>Responsable : {item.owner_name}</span>}
      {item.delivered_in?.length > 0 ? <span>{deliveredInText(item.delivered_in)}</span> : item.target_version && <span>Version {item.target_version}</span>}
      {pharmacies && <span>{pharmacies}</span>}
      {item.merged_count > 0 && <span>{item.merged_count} regroupé{item.merged_count > 1 ? "s" : ""}</span>}
    </div>
  );
}

function MobileRow({ item }) {
  return (
    <Link to={`/produit/${item.id}`} className="flex items-start gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`product-card-${item.number}`}>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="text-xs text-slate-500 tabular-nums">{item.number}</div>
        <div className="font-medium text-slate-900 break-words">{item.title}</div>
        <div className="flex flex-wrap gap-1.5"><TypeBadge type={item.type} /><StatusBadge status={item.status} /><PriorityBadge priority={item.priority} /></div>
        <MetaLine item={item} />
      </div>
      <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
    </Link>
  );
}

// The state is part of the card when the board is not grouped by state (otherwise the column already says it).
function RoadmapCard({ item, showStatus = false }) {
  return (
    <Link to={`/produit/${item.id}`} className="block rounded-lg border border-slate-200 bg-white p-3 space-y-1.5 hover:border-slate-300 hover:shadow-sm transition-shadow"
      data-testid={`product-roadmap-${item.number}`}>
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs text-slate-500 tabular-nums"><span className="whitespace-nowrap">{item.number}</span><PriorityBadge priority={item.priority} /></div>
      <div className="text-sm font-medium text-slate-900 break-words">{item.title}</div>
      <div className="flex flex-wrap items-center gap-1.5">
        {showStatus && <StatusBadge status={item.status} />}<TypeBadge type={item.type} />
        {item.status === "delivered" && <span className="text-xs text-slate-500">{formatDate(item.status_changed_at)}</span>}
      </div>
      <MetaLine item={item} />
    </Link>
  );
}

function Roadmap({ by, items }) {
  const groups = useMemo(() => groupRoadmap(items, by), [items, by]);
  if (by === "status") {
    return (
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5" data-testid="product-roadmap-board">
        {groups.map((g) => (
          // On a phone the columns are stacked: an empty one only costs scrolling, so it shows from the tablet up.
          <section key={g.key} className={`rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2 min-w-0 ${g.items.length === 0 ? "hidden md:block" : ""}`}
            data-testid={`product-roadmap-col-${g.key}`}>
            <h2 className="text-sm font-semibold text-slate-900 flex items-center justify-between">{g.label}<span className="text-xs font-normal text-slate-500">{g.items.length}</span></h2>
            {g.items.length === 0 ? <p className="text-xs text-slate-400">Rien ici.</p> : g.items.map((i) => <RoadmapCard key={i.id} item={i} />)}
          </section>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-5" data-testid="product-roadmap-groups">
      {groups.map((g) => (
        <section key={g.key} className="space-y-2" data-testid={`product-roadmap-group-${g.key}`}>
          <h2 className="text-sm font-semibold text-slate-900">{g.label} <span className="font-normal text-slate-500">· {g.items.length}</span></h2>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">{g.items.map((i) => <RoadmapCard key={i.id} item={i} showStatus />)}</div>
        </section>
      ))}
    </div>
  );
}

export default function ProductItems() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filters, update] = useFilters();
  const [searchInput, setSearchInput] = useState(filters.search);
  const [data, setData] = useState({ items: [], total: 0 });
  const [phase, setPhase] = useState("loading");
  const [errorText, setErrorText] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const seq = useRef(0);
  const roadmap = filters.tab === "roadmap";

  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== filters.search) update({ search: searchInput.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = useMemo(() => {
    const out = { limit: PAGE_SIZE };
    for (const key of ["status", "state", "type", "priority", "owner", "search"]) if (filters[key]) out[key] = filters[key];
    if (!out.state && !out.status) out.state = "open";
    return out;
  }, [filters.status, filters.state, filters.type, filters.priority, filters.owner, filters.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // A response that arrives after a newer request is dropped.
  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase("loading");
    try {
      const { data: body } = roadmap ? await api.get("/product/items/roadmap") : await api.get("/product/items", { params: query });
      if (mine !== seq.current) return;
      setData({ items: body.items, total: body.total ?? body.items.length });
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setErrorText(productErrorMessage(e, "Impossible de charger les retours."));
      setPhase("error");
    }
  }, [query, roadmap]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const loadMore = async () => {
    if (loadingMore) return;
    const mine = seq.current;
    setLoadingMore(true);
    try {
      const { data: body } = await api.get("/product/items", { params: { ...query, skip: data.items.length } });
      if (mine !== seq.current) return;
      setData((prev) => ({ ...body, items: [...prev.items, ...body.items.filter((i) => !prev.items.some((p) => p.id === i.id))] }));
    } catch (e) {
      if (mine === seq.current) toast.error(productErrorMessage(e, "Impossible de charger la suite."));
    } finally {
      if (mine === seq.current) setLoadingMore(false);
    }
  };

  const activeFilters = [filters.status, filters.type, filters.priority].filter(Boolean).length;
  const filtered = activeFilters > 0 || !!filters.owner || !!filters.search || (!!filters.state && filters.state !== "open");
  const reset = () => { setSearchInput(""); update({ status: "", state: "", type: "", priority: "", owner: "", search: "" }); };
  const quickActive = (view) => Object.entries(view.params).every(([k, v]) => filters[k] === v);
  const toggleQuick = (view) => {
    if (quickActive(view)) update(Object.fromEntries(Object.keys(view.params).map((k) => [k, ""])));
    else update({ status: "", state: "", owner: "", ...view.params });
  };
  const canCreate = hasPermission(user, PRODUCT_CREATE_KEYS);

  const selects = (
    <>
      <Select value={filters.status || ALL} onValueChange={(v) => update({ status: v === ALL ? "" : v, state: "" })}>
        <SelectTrigger className="h-9 w-full md:w-48" aria-label="État" data-testid="product-status-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Tous les états ouverts</SelectItem>
          {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={filters.type || ALL} onValueChange={(v) => update({ type: v === ALL ? "" : v })}>
        <SelectTrigger className="h-9 w-full md:w-48" aria-label="Type" data-testid="product-type-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Tous les types</SelectItem>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={filters.priority || ALL} onValueChange={(v) => update({ priority: v === ALL ? "" : v })}>
        <SelectTrigger className="h-9 w-full md:w-52" aria-label="Priorité" data-testid="product-priority-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Toutes les priorités</SelectItem>
          {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABELS[p]}</SelectItem>)}
        </SelectContent>
      </Select>
    </>
  );

  return (
    <div className="space-y-4" data-testid="product-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Retours et feuille de route</h1>
          <p className="text-sm text-slate-500">Ce que remontent les équipes et les pharmacies, ce qui est retenu, planifié et livré.</p>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && (
            <Button asChild data-testid="product-new"><Link to="/produit/nouveau"><Plus size={16} className="mr-1.5" />Nouveau retour</Link></Button>
          )}
        </div>
      </div>

      <div className="flex gap-1" role="tablist" aria-label="Vues du produit" data-testid="product-tabs">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={filters.tab === t.id} onClick={() => update({ tab: t.id === "items" ? "" : t.id })} data-testid={`product-tab-${t.id}`}
            className={`flex-auto sm:flex-none rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${filters.tab === t.id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {roadmap ? (
        <div className="flex gap-1 flex-wrap" role="group" aria-label="Regrouper la feuille de route">
          {ROADMAP_VIEWS.map((v) => (
            <Button key={v.id} variant={filters.by === v.id ? "default" : "outline"} size="sm" className="h-8" aria-pressed={filters.by === v.id}
              onClick={() => update({ by: v.id === "status" ? "" : v.id })} data-testid={`product-by-${v.id}`}>{v.label}</Button>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Numéro ou titre…" className="pl-9 h-9" aria-label="Rechercher un retour"
              data-testid="product-search" />
          </div>
          {QUICK_VIEWS.filter((view) => !view.permission || hasPermission(user, view.permission)).map((view) => (
            <Button key={view.id} variant={quickActive(view) ? "default" : "outline"} size="sm" className="h-9 shrink-0" aria-pressed={quickActive(view)}
              onClick={() => toggleQuick(view)} data-testid={`product-quick-${view.id}`}>{view.label}</Button>
          ))}
          <div className="hidden md:flex items-center gap-2">{selects}</div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="md:hidden h-9 gap-1.5 shrink-0" aria-label="Filtres" data-testid="product-filters-toggle">
                <SlidersHorizontal size={14} />{activeFilters > 0 && <span className="text-xs font-semibold">{activeFilters}</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" title="Filtres" className="w-72 space-y-3 md:hidden">{selects}</PopoverContent>
          </Popover>
          {filtered && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset} data-testid="product-reset"><X size={14} className="mr-1" />Effacer</Button>}
        </div>
      )}

      {phase === "loading" && <div className="space-y-2" data-testid="product-loading"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}

      {phase === "error" && (
        <Card className="p-6 text-center space-y-3" data-testid="product-error">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{errorText}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}

      {phase === "ready" && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="product-empty">
          <Lightbulb className="mx-auto text-slate-300" size={26} />
          {roadmap ? (
            <>
              <h3 className="font-medium text-slate-900">La feuille de route est vide</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">Les retours retenus, planifiés, en développement, en test ou récemment livrés apparaissent ici.</p>
            </>
          ) : filtered ? (
            <>
              <h3 className="font-medium text-slate-900">Aucun retour ne correspond</h3>
              <p className="text-sm text-slate-500">Modifiez ou effacez les filtres pour élargir la liste.</p>
              <Button variant="outline" size="sm" onClick={reset}>Effacer les filtres</Button>
            </>
          ) : (
            <>
              <h3 className="font-medium text-slate-900">Aucun retour pour le moment</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">Notez ici ce que remontent les pharmacies et les équipes : bugs, améliorations, demandes.</p>
              {canCreate && <Button asChild size="sm"><Link to="/produit/nouveau"><Plus size={14} className="mr-1.5" />Nouveau retour</Link></Button>}
            </>
          )}
        </Card>
      )}

      {phase === "ready" && data.items.length > 0 && roadmap && <Roadmap by={filters.by} items={data.items} />}

      {phase === "ready" && data.items.length > 0 && !roadmap && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="product-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Retour</TableHead><TableHead>État</TableHead><TableHead>Priorité</TableHead><TableHead>Responsable</TableHead>
                  <TableHead>Pharmacies</TableHead><TableHead className="hidden xl:table-cell">Mis à jour</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((i) => (
                  <TableRow key={i.id} className="cursor-pointer" onClick={() => navigate(`/produit/${i.id}`)} data-testid={`product-row-${i.number}`}>
                    <TableCell className="max-w-[26rem]">
                      <Link to={`/produit/${i.id}`} className="font-medium text-slate-900 hover:underline line-clamp-2" onClick={(e) => e.stopPropagation()}>{i.title}</Link>
                      <div className="text-xs text-slate-500 tabular-nums flex items-center gap-1.5 mt-0.5">{i.number}<TypeBadge type={i.type} /></div>
                    </TableCell>
                    <TableCell><StatusBadge status={i.status} /></TableCell>
                    <TableCell>{i.priority ? <PriorityBadge priority={i.priority} compact /> : <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-sm">{i.owner_name || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-sm tabular-nums">{i.impacted_pharmacies || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="hidden xl:table-cell text-sm tabular-nums text-slate-600">{formatDate(i.updated_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden overflow-hidden" data-testid="product-list-mobile">{data.items.map((i) => <MobileRow key={i.id} item={i} />)}</Card>
          <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
            <span data-testid="product-count">{data.items.length} sur {data.total} retour{data.total > 1 ? "s" : ""}</span>
            {data.items.length < data.total && (
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="product-load-more">
                {loadingMore ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}Charger plus
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
